"use client";

import React, { useEffect, useRef, useState } from "react";
import Icon from "@/components/ui/AppIcon";

const stats = [
  {
    value: 9125,
    suffix: "+",
    label: "Students Qualified",
    icon: "AcademicCapIcon",
    color: "#D4A853",
  },
  {
    value: 3194,
    suffix: "+",
    label: "Top Scorers (7+ Bands)",
    icon: "TrophyIcon",
    color: "#10B981",
  },
  {
    value: 1825,
    suffix: "+",
    label: "Visa Approvals",
    icon: "CheckBadgeIcon",
    color: "#3B82F6",
  },
  {
    value: 91,
    suffix: "%+",
    label: "Study Visa Success Rate",
    icon: "ChartBarIcon",
    color: "#8B5CF6",
  },
  {
    value: 91,
    suffix: "%+",
    label: "Success for Refused Cases",
    icon: "ArrowPathIcon",
    color: "#EF4444",
  },
];

const usps = [
  "Live & Online Classes",
  "Exam-like Mock Tests",
  "Constant Evaluation",
  "Study Material & Resources",
  "Separate Speaking Lab",
  "One-to-One Speaking",
  "Highly Experienced Staff",
  "Additional Grammar Classes",
  "Transparent Visa Process",
  "Payment After Visa Approval",
];

function CountUp({
  target,
  suffix,
  active,
}: {
  target: number;
  suffix: string;
  active: boolean;
}) {
  const [count, setCount] = useState(0);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (!active) return;
    const duration = 1800;
    const start = performance.now();
    const step = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [active, target]);

  return (
    <span data-component-id="src/app/home-page/components/StatsSection.tsx:87:4" data-component-path="src/app/home-page/components/StatsSection.tsx" data-component-line="87" data-component-end-line="90" data-component-file="StatsSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22stat-number%20tabular-nums%22%7D" className="stat-number tabular-nums">
      {count.toLocaleString("en-IN")}
      {suffix}
    </span>
  );
}

export default function StatsSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section data-component-id="src/app/home-page/components/StatsSection.tsx:115:4" data-component-path="src/app/home-page/components/StatsSection.tsx" data-component-line="115" data-component-end-line="211" data-component-file="StatsSection.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22py-20%20md%3Apy-28%20bg-navy%20relative%20overflow-hidden%22%7D"
      ref={sectionRef}
      className="py-20 md:py-28 bg-navy relative overflow-hidden"
      aria-labelledby="stats-heading"
    >
      {/* Background texture */}
      <div data-component-id="src/app/home-page/components/StatsSection.tsx:121:6" data-component-path="src/app/home-page/components/StatsSection.tsx" data-component-line="121" data-component-end-line="124" data-component-file="StatsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20opacity-5%22%7D" className="absolute inset-0 opacity-5">
        <div data-component-id="src/app/home-page/components/StatsSection.tsx:122:8" data-component-path="src/app/home-page/components/StatsSection.tsx" data-component-line="122" data-component-end-line="122" data-component-file="StatsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20top-0%20left-0%20w-96%20h-96%20rounded-full%20bg-gold%20blur-%5B120px%5D%22%7D" className="absolute top-0 left-0 w-96 h-96 rounded-full bg-gold blur-[120px]" />
        <div data-component-id="src/app/home-page/components/StatsSection.tsx:123:8" data-component-path="src/app/home-page/components/StatsSection.tsx" data-component-line="123" data-component-end-line="123" data-component-file="StatsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20bottom-0%20right-0%20w-80%20h-80%20rounded-full%20bg-blue-400%20blur-%5B100px%5D%22%7D" className="absolute bottom-0 right-0 w-80 h-80 rounded-full bg-blue-400 blur-[100px]" />
      </div>

      <div data-component-id="src/app/home-page/components/StatsSection.tsx:126:6" data-component-path="src/app/home-page/components/StatsSection.tsx" data-component-line="126" data-component-end-line="210" data-component-file="StatsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20z-10%20max-w-7xl%20mx-auto%20px-6%22%7D" className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Header */}
        <div data-component-id="src/app/home-page/components/StatsSection.tsx:128:8" data-component-path="src/app/home-page/components/StatsSection.tsx" data-component-line="128" data-component-end-line="145" data-component-file="StatsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
          className={`text-center mb-16 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
        >
          <span data-component-id="src/app/home-page/components/StatsSection.tsx:131:10" data-component-path="src/app/home-page/components/StatsSection.tsx" data-component-line="131" data-component-end-line="133" data-component-file="StatsSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22inline-block%20px-4%20py-1.5%20rounded-full%20bg-gold%2F15%20border%20border-gold%2F30%20text-gold%20text-xs%20font-600%20uppercase%20tracking-wider%20mb-4%22%2C%22textContent%22%3A%22Why%20Choose%20Dream'z%20Academy%3F%22%7D" className="inline-block px-4 py-1.5 rounded-full bg-gold/15 border border-gold/30 text-gold text-xs font-600 uppercase tracking-wider mb-4">
            Why Choose Dream'z Academy?
          </span>
          <h2 data-component-id="src/app/home-page/components/StatsSection.tsx:134:10" data-component-path="src/app/home-page/components/StatsSection.tsx" data-component-line="134" data-component-end-line="139" data-component-file="StatsSection.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22id%22%3A%22stats-heading%22%2C%22className%22%3A%22text-3xl%20md%3Atext-4xl%20lg%3Atext-5xl%20font-800%20text-white%20mb-4%22%2C%22textContent%22%3A%22We%20Don't%20Believe%20in%20Empty%20Promises%22%7D"
            id="stats-heading"
            className="text-3xl md:text-4xl lg:text-5xl font-800 text-white mb-4"
          >
            We Don't Believe in Empty Promises
          </h2>
          <p data-component-id="src/app/home-page/components/StatsSection.tsx:140:10" data-component-path="src/app/home-page/components/StatsSection.tsx" data-component-line="140" data-component-end-line="144" data-component-file="StatsSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-white%2F60%20max-w-2xl%20mx-auto%20text-lg%22%2C%22textContent%22%3A%22Here%20at%20Dream'z%20we%20believe%20in%20%20%20.%20We%5Cn%20%20%20%20%20%20%20%20%20%20%20%20promise%20to%20give%20you%20the%20best%20immigration%20experience.%22%7D" className="text-white/60 max-w-2xl mx-auto text-lg">
            Here at Dream'z we believe in{" "}
            <strong data-component-id="src/app/home-page/components/StatsSection.tsx:142:12" data-component-path="src/app/home-page/components/StatsSection.tsx" data-component-line="142" data-component-end-line="142" data-component-file="StatsSection.tsx" data-component-name="strong" data-component-content="%7B%22elementName%22%3A%22strong%22%2C%22className%22%3A%22text-gold%22%2C%22textContent%22%3A%22Delivering%20Results%22%7D" className="text-gold">Delivering Results</strong>. We
            promise to give you the best immigration experience.
          </p>
        </div>

        {/* Stats grid */}
        <div data-component-id="src/app/home-page/components/StatsSection.tsx:148:8" data-component-path="src/app/home-page/components/StatsSection.tsx" data-component-line="148" data-component-end-line="185" data-component-file="StatsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-2%20md%3Agrid-cols-3%20lg%3Agrid-cols-5%20gap-5%20mb-16%22%7D" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5 mb-16">
          {stats.map((stat, i) => (
            <div data-component-id="src/app/home-page/components/StatsSection.tsx:150:12" data-component-path="src/app/home-page/components/StatsSection.tsx" data-component-line="150" data-component-end-line="183" data-component-file="StatsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
              key={stat.label}
              className={`relative bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5 text-center transition-all duration-700 hover:bg-white/10 hover:border-white/20 ${
                visible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-8"
              }`}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <div data-component-id="src/app/home-page/components/StatsSection.tsx:159:14" data-component-path="src/app/home-page/components/StatsSection.tsx" data-component-line="159" data-component-end-line="169" data-component-file="StatsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-12%20h-12%20rounded-xl%20mx-auto%20mb-4%20flex%20items-center%20justify-center%22%7D"
                className="w-12 h-12 rounded-xl mx-auto mb-4 flex items-center justify-center"
                style={{ backgroundColor: `${stat.color}20` }}
              >
                <Icon data-component-id="src/app/home-page/components/StatsSection.tsx:163:16" data-component-path="src/app/home-page/components/StatsSection.tsx" data-component-line="163" data-component-end-line="168" data-component-file="StatsSection.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22className%22%3A%22%22%7D"
                  name={stat.icon as any}
                  size={24}
                  className=""
                  style={{ color: stat.color }}
                />
              </div>
              <p data-component-id="src/app/home-page/components/StatsSection.tsx:170:14" data-component-path="src/app/home-page/components/StatsSection.tsx" data-component-line="170" data-component-end-line="179" data-component-file="StatsSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-3xl%20md%3Atext-4xl%20font-800%20mb-1%22%7D"
                className="text-3xl md:text-4xl font-800 mb-1"
                style={{ color: stat.color }}
              >
                <CountUp data-component-id="src/app/home-page/components/StatsSection.tsx:174:16" data-component-path="src/app/home-page/components/StatsSection.tsx" data-component-line="174" data-component-end-line="178" data-component-file="StatsSection.tsx" data-component-name="CountUp" data-component-content="%7B%22elementName%22%3A%22CountUp%22%7D"
                  target={stat.value}
                  suffix={stat.suffix}
                  active={visible}
                />
              </p>
              <p data-component-id="src/app/home-page/components/StatsSection.tsx:180:14" data-component-path="src/app/home-page/components/StatsSection.tsx" data-component-line="180" data-component-end-line="182" data-component-file="StatsSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20text-white%2F60%20leading-tight%22%7D" className="text-xs text-white/60 leading-tight">
                {stat.label}
              </p>
            </div>
          ))}
        </div>

        {/* USPs */}
        <div data-component-id="src/app/home-page/components/StatsSection.tsx:188:8" data-component-path="src/app/home-page/components/StatsSection.tsx" data-component-line="188" data-component-end-line="209" data-component-file="StatsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
          className={`transition-all duration-700 delay-500 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
        >
          <p data-component-id="src/app/home-page/components/StatsSection.tsx:191:10" data-component-path="src/app/home-page/components/StatsSection.tsx" data-component-line="191" data-component-end-line="193" data-component-file="StatsSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-center%20text-white%2F50%20text-sm%20uppercase%20tracking-wider%20mb-6%22%2C%22textContent%22%3A%22Emerged%20as%20one%20of%20the%20top%20institutes%20in%20the%20city%20focused%20on%3A%22%7D" className="text-center text-white/50 text-sm uppercase tracking-wider mb-6">
            Emerged as one of the top institutes in the city focused on:
          </p>
          <div data-component-id="src/app/home-page/components/StatsSection.tsx:194:10" data-component-path="src/app/home-page/components/StatsSection.tsx" data-component-line="194" data-component-end-line="208" data-component-file="StatsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-wrap%20justify-center%20gap-3%22%7D" className="flex flex-wrap justify-center gap-3">
            {usps.map((usp) => (
              <span data-component-id="src/app/home-page/components/StatsSection.tsx:196:14" data-component-path="src/app/home-page/components/StatsSection.tsx" data-component-line="196" data-component-end-line="206" data-component-file="StatsSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22flex%20items-center%20gap-2%20px-4%20py-2%20rounded-full%20bg-white%2F8%20border%20border-white%2F15%20text-sm%20text-white%2F80%20hover%3Abg-white%2F12%20hover%3Atext-white%20transition-all%22%7D"
                key={usp}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/8 border border-white/15 text-sm text-white/80 hover:bg-white/12 hover:text-white transition-all"
              >
                <Icon data-component-id="src/app/home-page/components/StatsSection.tsx:200:16" data-component-path="src/app/home-page/components/StatsSection.tsx" data-component-line="200" data-component-end-line="204" data-component-file="StatsSection.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22CheckCircleIcon%22%2C%22className%22%3A%22text-gold%20flex-shrink-0%22%7D"
                  name="CheckCircleIcon"
                  size={14}
                  className="text-gold flex-shrink-0"
                />
                {usp}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
